import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Popover from "@radix-ui/react-popover";
import { differenceInCalendarDays, parseISO } from "date-fns";
import {
  AppWindow,
  CalendarDays,
  ChevronDown,
  Clock,
  Hash,
  ListTodo,
  RotateCcw,
  SlidersHorizontal,
  User,
  X,
} from "lucide-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDuration, dayKeyForDate } from "../../utils";
import type { UserFilterOption } from "../UserFilterMultiSelect";
import ProcessPeopleFilter from "./ProcessPeopleFilter";
import { AppsEditor, DateEditor, NodeEditor, QueryEditor, UserEditor } from "./ProcessScopeEditors";
import ProcessScopeRangeEditor from "./ProcessScopeRangeEditor";
import {
  type InstanceScope,
  EMPTY_INSTANCE_SCOPE,
  activeScopeCriteria,
  withoutScopeCriterion,
} from "./processScopeModel";
import { FilterChip } from "./processSidebarUi";
import type { ProcessGraphState } from "./useProcessGraph";

type FilterKind = "user" | "date" | "node" | "apps" | "duration" | "steps";
const MORE_FILTERS: FilterKind[] = ["apps", "node", "duration", "steps"];

/** The page's scope: which cases the graph, every tab and the readings above the list describe. */
export default function ProcessFilters({
  state,
  peopleOptions,
}: {
  state: ProcessGraphState;
  peopleOptions?: UserFilterOption[];
}) {
  const { t, i18n } = useTranslation();
  const draft = { ...state.appliedScope, commonPaths: false, excludedIds: [] };
  const changeFilter = (next: InstanceScope) =>
    state.applyScope({
      ...next,
      commonPaths: state.appliedScope.commonPaths,
      excludedIds: state.appliedScope.excludedIds,
    });
  const [editor, setEditor] = useState<FilterKind | null>(null);
  // A filter picked from the menu opens its editor on its chip, so the menu must not pull the
  // focus back to its trigger on the way out: the editor would read that as a click outside.
  const pickedFromMenu = useRef(false);
  const formatDate = (day: string) =>
    new Intl.DateTimeFormat(i18n?.resolvedLanguage, {
      day: "numeric",
      month: "short",
      ...(day.slice(0, 4) !== String(new Date().getFullYear()) ? { year: "numeric" } : {}),
    }).format(parseISO(day));
  const today = dayKeyForDate(new Date());
  const recentDays =
    draft.from && (draft.to ?? today) === today
      ? differenceInCalendarDays(parseISO(today), parseISO(draft.from)) + 1
      : 0;
  const dateLabel =
    recentDays > 0
      ? t("last_n_days", { count: recentDays })
      : draft.from && draft.to
        ? `${formatDate(draft.from)} – ${formatDate(draft.to)}`
        : draft.from || draft.to
          ? `${t(draft.from ? "from" : "to")} ${formatDate(draft.from || draft.to || "")}`
          : "";
  const rangeLabel = (
    min: number | undefined,
    max: number | undefined,
    format: (value: number) => string,
  ) =>
    min !== undefined && max !== undefined
      ? `${format(min)} – ${format(max)}`
      : min !== undefined
        ? `≥ ${format(min)}`
        : max !== undefined
          ? `≤ ${format(max)}`
          : "";
  const filters: { kind: FilterKind; Icon: typeof User; label: string; summary: string }[] = [
    {
      kind: "user",
      Icon: User,
      label: t("scope_filter_user"),
      summary:
        draft.userIds.length === 1
          ? state.process.sources.find(
              source => source.userId === draft.userIds[0] && source.userName?.trim(),
            )?.userName || t("user")
          : draft.userIds.length
            ? t("dashboard.people_selected", { count: draft.userIds.length })
            : "",
    },
    { kind: "date", Icon: CalendarDays, label: t("processPage.dates"), summary: dateLabel },
    {
      kind: "apps",
      Icon: AppWindow,
      label: t("processPage.apps"),
      summary: draft.apps?.length ? t("processPage.appCount", { count: draft.apps.length }) : "",
    },
    {
      kind: "node",
      Icon: ListTodo,
      label: t("processPage.containsStep"),
      summary: draft.nodeIds.length ? t("n_steps", { count: draft.nodeIds.length }) : "",
    },
    {
      kind: "duration",
      Icon: Clock,
      label: t("processPanel.columnDuration"),
      summary: rangeLabel(draft.minDuration, draft.maxDuration, formatDuration),
    },
    {
      kind: "steps",
      Icon: Hash,
      label: t("processPage.stepCount"),
      summary: rangeLabel(draft.minSteps, draft.maxSteps, value => t("n_steps", { count: value })),
    },
  ];
  const activeFilters = activeScopeCriteria({
    ...draft,
    userIds: peopleOptions?.every(person => draft.userIds.includes(person.id)) ? [] : draft.userIds,
  });
  const renderFilter = ({ kind, Icon, label, summary }: (typeof filters)[number]) => (
    <div
      key={kind}
      className={`flex min-w-0 items-center rounded-full border border-outline ${kind === "user" ? "max-w-60" : "max-w-full"} ${summary ? "bg-muted-surface" : "bg-surface"}`}
    >
      <Popover.Root open={editor === kind} onOpenChange={value => setEditor(value ? kind : null)}>
        <Popover.Trigger
          type="button"
          aria-label={summary ? `${label}: ${summary}` : label}
          title={summary || label}
          className="flex h-[30px] min-w-0 items-center gap-2 rounded-full px-3 text-xs font-medium text-text outline-none transition hover:bg-muted-surface focus-visible:ring-2 focus-visible:ring-info/20"
        >
          <Icon className="h-4 w-4 shrink-0 text-text-dim" aria-hidden="true" />
          <span className={`truncate ${summary ? "font-medium" : ""}`}>{summary || label}</span>
          {!summary && (
            <ChevronDown className="h-3 w-3 shrink-0 text-text-dim" aria-hidden="true" />
          )}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={6}
            collisionPadding={16}
            aria-label={label}
            className={`pd-theme dropdown-menu max-h-[var(--radix-popover-content-available-height)] max-w-[calc(100vw-32px)] overflow-y-auto ${kind === "date" ? "w-80 p-3" : kind === "node" ? "w-[28rem]" : "w-80"}`}
          >
            {(kind === "duration" || kind === "steps") && (
              <ProcessScopeRangeEditor
                kind={kind}
                scope={draft}
                onChange={changeFilter}
                onPicked={() => setEditor(null)}
              />
            )}
            {kind === "user" && (
              <UserEditor
                instances={state.process.instances}
                scope={draft}
                onChange={changeFilter}
                onPicked={() => setEditor(null)}
              />
            )}
            {kind === "date" && (
              <DateEditor
                index={state.scopeIndex}
                scope={draft}
                onChange={changeFilter}
                onPicked={() => setEditor(null)}
              />
            )}
            {kind === "node" && (
              <NodeEditor
                process={state.process}
                scope={draft}
                onChange={changeFilter}
                onPicked={() => setEditor(null)}
              />
            )}
            {kind === "apps" && (
              <AppsEditor
                instances={state.process.instances}
                scope={draft}
                onChange={changeFilter}
                onPicked={() => setEditor(null)}
              />
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {summary && (
        <button
          type="button"
          aria-label={t("processPage.clearFilter", { filter: label })}
          onClick={() => {
            changeFilter(withoutScopeCriterion(draft, kind));
            setEditor(null);
          }}
          className="mr-1 shrink-0 rounded-full p-1 text-text-dim hover:bg-surface hover:text-text focus-visible:ring-2 focus-visible:ring-info/20"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );

  const buttonClass =
    "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium outline-none transition focus-visible:ring-2 focus-visible:ring-info/20";
  const moreFilters = filters.filter(filter => MORE_FILTERS.includes(filter.kind));
  return (
    <Popover.Root open={state.filtersOpen} onOpenChange={state.setFiltersOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={t("filters")}
          title={t("processPanel.scopeHelp")}
          data-tour="case-ai-filter"
          className={`pointer-events-auto inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs shadow-sm focus-visible:ring-2 focus-visible:ring-info/25 ${activeFilters.length ? "border-info/30 bg-[color-mix(in_srgb,var(--color-info)_10%,var(--color-surface))] text-info hover:bg-[color-mix(in_srgb,var(--color-info)_15%,var(--color-surface))]" : "border-outline bg-surface text-text hover:bg-surface-strong"}`}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
          {t("filters")}
          {activeFilters.length > 0 && (
            <span className="rounded-full bg-info/10 px-1.5 tabular-nums">
              {activeFilters.length}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className="pd-theme z-50 w-[32rem] max-w-[calc(100vw-24px)] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto rounded-lg border border-outline bg-surface p-4 text-text shadow-xl"
        >
          <section aria-label={t("filters")} className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium">{t("filters")}</h3>
              <span
                className="ml-auto whitespace-nowrap text-xs tabular-nums text-text-mid"
                aria-live="polite"
              >
                {activeFilters.length > 0
                  ? t("scope_instance_count", {
                      selected: state.filteredInstances.length,
                      total: state.scopeIndex.order.length,
                    })
                  : t("n_cases", { count: state.filteredInstances.length })}
              </span>
              {activeFilters.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    state.resetScopeQuery();
                    changeFilter({ ...EMPTY_INSTANCE_SCOPE });
                  }}
                  className="inline-flex h-6 items-center gap-1 rounded-full px-2 text-xs text-text-mid hover:text-text focus-visible:ring-2 focus-visible:ring-info/20"
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("process_directory.clear_filters")}
                </button>
              )}
            </div>
            <p className="text-xs text-text-dim">{t("processPanel.scopeHelp")}</p>
            <div className="flex flex-wrap items-center gap-1.5">
              {peopleOptions && (
                <ProcessPeopleFilter
                  scope={draft}
                  userOptions={peopleOptions}
                  onChange={changeFilter}
                />
              )}
              {filters
                .filter(
                  filter =>
                    !MORE_FILTERS.includes(filter.kind) &&
                    (!peopleOptions || filter.kind !== "user"),
                )
                .map(renderFilter)}
              {/* Only the filters in use take room in the row; the menu adds one and opens its editor. */}
              {moreFilters
                .filter(filter => filter.summary || editor === filter.kind)
                .map(renderFilter)}
              <DropdownMenu.Root modal={false}>
                <DropdownMenu.Trigger
                  className={`${buttonClass} border-outline bg-surface text-text hover:bg-muted-surface`}
                >
                  <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("processPage.moreFilters")}
                  <ChevronDown className="h-3 w-3 text-text-dim" aria-hidden="true" />
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    align="start"
                    sideOffset={4}
                    collisionPadding={16}
                    onCloseAutoFocus={event => {
                      if (pickedFromMenu.current) event.preventDefault();
                      pickedFromMenu.current = false;
                    }}
                    className="pd-theme dropdown-menu min-w-52 p-1"
                  >
                    {moreFilters.map(({ kind, Icon, label, summary }) => (
                      <DropdownMenu.Item
                        key={kind}
                        onSelect={() => {
                          pickedFromMenu.current = true;
                          setEditor(kind);
                        }}
                        className="dropdown-option flex items-center gap-2 text-sm"
                      >
                        <Icon className="h-4 w-4 shrink-0 text-text-dim" aria-hidden="true" />
                        <span className="flex-1">{label}</span>
                        {summary && (
                          <span className="truncate text-xs text-text-dim">{summary}</span>
                        )}
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
            <QueryEditor
              scope={draft}
              status={state.scopeQueryStatus}
              onChange={changeFilter}
              onResolve={state.resolveScopeQuery}
              onReset={state.resetScopeQuery}
              onDone={() => state.setFiltersOpen(false)}
            />
            {draft.query && (
              <div data-tour="nl-filter-active" className="space-y-1">
                <FilterChip
                  label={draft.query.text}
                  onClear={() => {
                    state.resetScopeQuery();
                    changeFilter(withoutScopeCriterion(draft, "query"));
                  }}
                />
                {draft.query.explanation && (
                  <p className="text-xs text-text-dim">{draft.query.explanation}</p>
                )}
              </div>
            )}
          </section>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
